import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LifeBuoy, ArrowLeft, Globe, Mail, Phone, Clock, MessageCircle, ChevronDown, ChevronUp } from 'lucide-react';

// Page Support publique - accessible sans authentification
// Exigence pour publication Apple App Store et Google Play Store
// Contact + FAQ + informations legales

const CONTENT = {
  fr: {
    title: 'Centre de support',
    subtitle: 'Nous sommes là pour vous aider',
    lastUpdate: 'Support disponible du lundi au vendredi',
    heroText: "Vous avez une question, un problème ou une suggestion? Contactez-nous par courriel, téléphone ou consultez notre foire aux questions ci-dessous.",
    contactTitle: 'Nous joindre',
    emailLabel: 'Courriel',
    emailValue: 'support@propilote.ca',
    phoneLabel: 'Téléphone',
    phoneValue: 'Sur demande par courriel',
    hoursLabel: 'Heures de support',
    hoursValue: 'Lundi au vendredi, 8h à 17h (heure de l\'Est)',
    responseLabel: 'Délai de réponse habituel',
    responseValue: 'Moins de 24 heures ouvrables',
    faqTitle: 'Foire aux questions',
    faqIntro: 'Cliquez sur une question pour voir la réponse.',
    faq: [
      {
        q: 'Comment créer un compte chauffeur?',
        a: "Votre gestionnaire de flotte vous envoie une invitation par courriel. Cliquez sur le lien reçu, choisissez un mot de passe et vous êtes prêt à commencer. Si vous n'avez pas reçu l'invitation, vérifiez votre dossier de courriels indésirables ou contactez votre gestionnaire.",
      },
      {
        q: 'J\'ai oublié mon mot de passe, que faire?',
        a: "Sur la page de connexion, cliquez sur \"Mot de passe oublié\". Vous recevrez un lien par courriel pour le réinitialiser. Si le problème persiste, votre gestionnaire de flotte peut réinitialiser votre mot de passe temporairement depuis son tableau de bord administrateur.",
      },
      {
        q: 'L\'application fonctionne-t-elle sans connexion Internet?',
        a: "Oui. ProPilote est conçue en mode hors ligne (\"offline-first\") : vous pouvez continuer à créer des rapports, effectuer des inspections et enregistrer des livraisons même sans signal. Vos données seront automatiquement synchronisées dès que la connexion sera rétablie.",
      },
      {
        q: 'Comment installer ProPilote sur mon téléphone?',
        a: "ProPilote est une application web progressive (PWA) qui s'installe directement depuis votre navigateur : \n1. Ouvrez propilote.ca dans Safari (iPhone) ou Chrome (Android).\n2. Cliquez sur le menu de partage ou les trois points en haut.\n3. Sélectionnez \"Ajouter à l'écran d'accueil\".\n4. L'icône apparaît sur votre écran principal, prête à être utilisée comme une vraie app.",
      },
      {
        q: 'Puis-je annuler mon abonnement en tout temps?',
        a: "Oui. Vous pouvez annuler votre abonnement à tout moment depuis votre tableau de bord administrateur, section \"Facturation\". L'annulation prend effet à la fin de la période de facturation en cours. Aucun frais de résiliation ne s'applique.",
      },
      {
        q: 'Mes données sont-elles sécurisées?',
        a: "Absolument. Toutes les données sont chiffrées en transit (HTTPS/TLS 1.3) et au repos. Nos serveurs sont hébergés au Canada, conformément aux exigences de la Loi 25 du Québec et de la LPRPDE fédérale. Consultez notre politique de confidentialité pour plus de détails.",
      },
      {
        q: 'Comment ajouter un nouveau chauffeur à ma flotte?',
        a: "Depuis votre tableau de bord administrateur, allez dans la section \"Chauffeurs\" et cliquez sur \"Inviter un chauffeur\". Entrez son adresse courriel et l'invitation lui sera envoyée automatiquement. Il pourra créer son compte en cliquant sur le lien reçu.",
      },
      {
        q: 'Combien coûte l\'application?',
        a: "ProPilote fonctionne avec un modèle de tarification modulaire : vous ne payez que pour les modules dont vous avez besoin. Un chauffeur individuel peut créer un compte gratuit avec un plafond de 20 rapports par mois. Pour connaître les tarifs des flottes, consultez notre page de tarification ou contactez-nous.",
      },
      {
        q: 'Que faire si l\'application plante ou affiche une erreur?',
        a: "Notez ce que vous étiez en train de faire, prenez une capture d'écran de l'erreur si possible, puis envoyez-nous un courriel à support@propilote.ca. Nous investiguons chaque incident dans les 24 heures. Redémarrer l'application (fermer et rouvrir) résout aussi la majorité des problèmes temporaires.",
      },
      {
        q: 'Puis-je exporter mes données?',
        a: "Oui. Vous pouvez exporter vos rapports, factures et inspections en format PDF ou CSV directement depuis les pages concernées de votre tableau de bord. Aucune limite d'exportation.",
      },
      {
        q: 'Est-ce que ProPilote est compatible avec ELD/DVIR?',
        a: "ProPilote se concentre actuellement sur la gestion opérationnelle (rapports, inspections, facturation, dispatch). Nous n'offrons pas encore d'intégration ELD certifiée. Cette fonctionnalité est envisagée pour une future version.",
      },
      {
        q: 'Comment supprimer mon compte définitivement?',
        a: "Pour demander la suppression complète de votre compte et de vos données personnelles, envoyez-nous un courriel à support@propilote.ca avec l'objet \"Demande de suppression de compte\". Nous traiterons votre demande dans un délai de 30 jours conformément à la Loi 25.",
      },
    ],
    resourcesTitle: 'Ressources utiles',
    resources: [
      { label: 'Politique de confidentialité', url: '/privacy' },
      { label: "Conditions d'utilisation", url: '/terms' },
      { label: "Page d'accueil", url: '/' },
    ],
    footer: "Vous ne trouvez pas la réponse à votre question? Envoyez-nous un courriel à support@propilote.ca et un membre de notre équipe vous répondra rapidement.",
    backButton: 'Retour',
  },
  en: {
    title: 'Support Center',
    subtitle: "We're here to help",
    lastUpdate: 'Support available Monday to Friday',
    heroText: 'Have a question, an issue, or a suggestion? Contact us by email, phone, or check our FAQ below.',
    contactTitle: 'Contact us',
    emailLabel: 'Email',
    emailValue: 'support@propilote.ca',
    phoneLabel: 'Phone',
    phoneValue: 'Available upon email request',
    hoursLabel: 'Support hours',
    hoursValue: 'Monday to Friday, 8am to 5pm (Eastern Time)',
    responseLabel: 'Typical response time',
    responseValue: 'Less than 24 business hours',
    faqTitle: 'Frequently asked questions',
    faqIntro: 'Click a question to see the answer.',
    faq: [
      {
        q: 'How do I create a driver account?',
        a: "Your fleet manager sends you an invitation by email. Click the link received, choose a password and you're ready to start. If you haven't received the invitation, check your spam folder or contact your fleet manager.",
      },
      {
        q: 'I forgot my password, what should I do?',
        a: 'On the login page, click "Forgot password". You will receive a link by email to reset it. If the problem persists, your fleet manager can reset your password temporarily from their admin dashboard.',
      },
      {
        q: 'Does the app work without an Internet connection?',
        a: 'Yes. ProPilote is designed offline-first: you can continue creating reports, doing inspections and recording deliveries even without signal. Your data will be automatically synchronized as soon as the connection is restored.',
      },
      {
        q: 'How do I install ProPilote on my phone?',
        a: "ProPilote is a Progressive Web App (PWA) that installs directly from your browser:\n1. Open propilote.ca in Safari (iPhone) or Chrome (Android).\n2. Click the share menu or the three dots at the top.\n3. Select \"Add to Home Screen\".\n4. The icon appears on your home screen, ready to use like a real app.",
      },
      {
        q: 'Can I cancel my subscription at any time?',
        a: 'Yes. You can cancel your subscription at any time from your admin dashboard, "Billing" section. The cancellation takes effect at the end of the current billing period. No cancellation fee applies.',
      },
      {
        q: 'Is my data secure?',
        a: 'Absolutely. All data is encrypted in transit (HTTPS/TLS 1.3) and at rest. Our servers are hosted in Canada, complying with Quebec Law 25 and federal PIPEDA requirements. See our privacy policy for details.',
      },
      {
        q: 'How do I add a new driver to my fleet?',
        a: 'From your admin dashboard, go to the "Drivers" section and click "Invite a driver". Enter their email address and the invitation will be sent automatically. They will be able to create their account by clicking the received link.',
      },
      {
        q: 'How much does the app cost?',
        a: 'ProPilote uses a modular pricing model: you only pay for the modules you need. An individual driver can create a free account with a limit of 20 reports per month. For fleet pricing, see our pricing page or contact us.',
      },
      {
        q: 'What should I do if the app crashes or shows an error?',
        a: 'Note what you were doing, take a screenshot of the error if possible, then send us an email at support@propilote.ca. We investigate every incident within 24 hours. Restarting the app (close and reopen) also solves most temporary issues.',
      },
      {
        q: 'Can I export my data?',
        a: 'Yes. You can export your reports, invoices and inspections in PDF or CSV format directly from the relevant pages of your dashboard. No export limit.',
      },
      {
        q: 'Is ProPilote compatible with ELD/DVIR?',
        a: 'ProPilote currently focuses on operational management (reports, inspections, invoicing, dispatch). We do not yet offer certified ELD integration. This feature is planned for a future version.',
      },
      {
        q: 'How do I permanently delete my account?',
        a: 'To request the complete deletion of your account and personal data, send us an email at support@propilote.ca with the subject "Account deletion request". We will process your request within 30 days in accordance with Quebec Law 25.',
      },
    ],
    resourcesTitle: 'Useful resources',
    resources: [
      { label: 'Privacy Policy', url: '/privacy' },
      { label: 'Terms of Use', url: '/terms' },
      { label: 'Home page', url: '/' },
    ],
    footer: "Can't find the answer to your question? Send us an email at support@propilote.ca and a team member will respond promptly.",
    backButton: 'Back',
  },
};

export default function SupportPage() {
  const navigate = useNavigate();
  const [lang, setLang] = useState('fr');
  const [openIndex, setOpenIndex] = useState(null);
  const t = CONTENT[lang];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/60 backdrop-blur sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            data-testid="support-back-btn"
          >
            <ArrowLeft size={18} />
            {t.backButton}
          </button>

          <button
            onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
            data-testid="support-lang-toggle"
          >
            <Globe size={16} />
            {lang === 'fr' ? 'English' : 'Français'}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10">
        {/* Hero */}
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/30">
            <LifeBuoy className="text-blue-400" size={28} />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-1">{t.title}</h1>
            <p className="text-slate-400">{t.subtitle}</p>
            <p className="text-xs text-slate-500 mt-1">{t.lastUpdate}</p>
          </div>
        </div>

        <p className="text-slate-300 text-lg leading-relaxed mb-10">{t.heroText}</p>

        {/* Contact card */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-10">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <MessageCircle size={20} className="text-blue-400" />
            {t.contactTitle}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Mail size={18} className="text-slate-400 mt-0.5" />
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">{t.emailLabel}</div>
                <a
                  href={`mailto:${t.emailValue}`}
                  className="text-blue-400 hover:text-blue-300 font-medium break-all"
                  data-testid="support-email-link"
                >
                  {t.emailValue}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone size={18} className="text-slate-400 mt-0.5" />
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">{t.phoneLabel}</div>
                <div className="text-slate-300">{t.phoneValue}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock size={18} className="text-slate-400 mt-0.5" />
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">{t.hoursLabel}</div>
                <div className="text-slate-300">{t.hoursValue}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MessageCircle size={18} className="text-slate-400 mt-0.5" />
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">{t.responseLabel}</div>
                <div className="text-slate-300">{t.responseValue}</div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <h2 className="text-2xl font-semibold text-white mb-2">{t.faqTitle}</h2>
        <p className="text-slate-400 text-sm mb-6">{t.faqIntro}</p>

        <div className="space-y-2 mb-10">
          {t.faq.map((item, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={idx}
                className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden transition-all"
                data-testid={`faq-item-${idx}`}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : idx)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-800/50 transition-colors"
                  data-testid={`faq-toggle-${idx}`}
                >
                  <span className="text-white font-medium pr-4">{item.q}</span>
                  {isOpen ? (
                    <ChevronUp size={18} className="text-slate-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown size={18} className="text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pt-2 text-slate-300 text-sm whitespace-pre-line leading-relaxed border-t border-slate-800">
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Resources */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-10">
          <h2 className="text-lg font-semibold text-white mb-4">{t.resourcesTitle}</h2>
          <div className="flex flex-wrap gap-3">
            {t.resources.map((r, idx) => (
              <a
                key={idx}
                href={r.url}
                className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors text-sm"
                data-testid={`resource-link-${idx}`}
              >
                {r.label}
              </a>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-blue-950/30 border border-blue-800/40 rounded-xl p-6 text-center">
          <p className="text-slate-300">{t.footer}</p>
          <a
            href={`mailto:${t.emailValue}`}
            className="inline-flex items-center gap-2 mt-4 px-6 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
            data-testid="support-contact-cta"
          >
            <Mail size={18} />
            {t.emailValue}
          </a>
        </div>

        <div className="text-center text-xs text-slate-600 mt-10">
          © 2026 Express Pat Morin inc. — ProPilote
        </div>
      </div>
    </div>
  );
}
